import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { getConsent, saveConsent } from "@/lib/consent";

const Btn = ({ testId, onClick, children }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className="w-full rounded-full border-2 border-slate-900 bg-white px-4 py-2.5 text-sm font-bold text-slate-900 transition-colors hover:bg-slate-900 hover:text-white">
    {children}
  </button>
);

export const CookieBanner = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => !getConsent());
  const [custom, setCustom] = useState(false);
  const [ads, setAds] = useState(() => getConsent()?.ads === true);

  useEffect(() => {
    const reopen = () => { setAds(getConsent()?.ads === true); setCustom(true); setOpen(true); };
    window.addEventListener("climnova-open-cookies", reopen);
    return () => window.removeEventListener("climnova-open-cookies", reopen);
  }, []);

  if (!open || pathname.startsWith("/admin")) return null;
  const choose = (value) => { saveConsent(value); setOpen(false); setCustom(false); };

  return (
    <div role="dialog" aria-label="Gestion des cookies" data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md z-[70] rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md p-5 shadow-2xl">
      <div className="flex items-center gap-2 font-display text-lg font-extrabold text-slate-900">
        <Cookie className="w-5 h-5 text-orange-600" /> Vos choix en matière de cookies
      </div>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        Nous utilisons des cookies nécessaires au fonctionnement du site et, avec votre accord, le pixel Meta pour mesurer l'efficacité de nos publicités.
        Vous pouvez changer d'avis à tout moment via « Gérer les cookies » en bas de page.{" "}
        <Link to="/politique-confidentialite" data-testid="cookie-banner-privacy-link" className="text-sky-700 underline">En savoir plus</Link>
      </p>
      {custom && (
        <div data-testid="cookie-panel" className="mt-4 flex flex-col gap-3 rounded-xl bg-slate-50 p-4 text-sm">
          <label className="flex items-center justify-between gap-3">
            <span><strong className="text-slate-900">Strictement nécessaires</strong><br /><span className="text-xs text-slate-500">Panier, paiement, sécurité. Toujours actifs.</span></span>
            <Switch checked disabled data-testid="cookie-necessary-switch" />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span><strong className="text-slate-900">Mesure publicitaire (Meta)</strong><br /><span className="text-xs text-slate-500">Pixel Meta : mesure des visites et conversions issues de nos publicités.</span></span>
            <Switch checked={ads} onCheckedChange={setAds} data-testid="cookie-ads-switch" />
          </label>
          <button type="button" onClick={() => choose(ads)} data-testid="cookie-save-button" className="btn-heat !py-2.5 text-sm">Enregistrer mes choix</button>
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Btn testId="cookie-reject-all-button" onClick={() => choose(false)}>Tout refuser</Btn>
        <Btn testId="cookie-accept-all-button" onClick={() => choose(true)}>Tout accepter</Btn>
      </div>
      {!custom && (
        <button type="button" data-testid="cookie-customize-button" onClick={() => setCustom(true)}
          className="mt-3 w-full text-center text-sm font-semibold text-slate-700 underline underline-offset-4 hover:text-slate-900">
          Personnaliser
        </button>
      )}
    </div>
  );
};
